import { events } from "@constants/tracking";
import { useExamContext } from "@features/exam";
import i18n from "@helpers/lib/i18n";
import useAnalytics from "@hooks/useAnalytics";
import { useProductDetails, useProductMetadata } from "@hooks/useProduct";
import { getPremiumContext, getRecommendedPlan } from "@memorang/helpers";
import type { Plan, ProductType } from "@memorang/types";
import { Box, Container, Loader } from "@memorang/ui";
import { router, useLocalSearchParams } from "expo-router";
import { useCallback, useEffect, useState } from "react";
import { ScrollView } from "react-native";
import IAPModalView from "../../components/IAPModal/IAPModalView";
import { getExamDaysLeft } from "../../helpers/stats";

export type PurchaseContainerBaseProps = {
	handleClose?: () => void;
	fromHomePage?: boolean;
	productType?: ProductType;
	handlePurchase: (selectedPlan?: Plan) => void;
	handleShowRestorePurchaseDialog: () => void;
	purchaseInProgress: boolean;
};

const PurchaseContainerBase = ({
	handleClose,
	fromHomePage,
	productType,
	handlePurchase,
	handleShowRestorePurchaseDialog,
	purchaseInProgress,
}: PurchaseContainerBaseProps) => {
	const { numItems } = useLocalSearchParams();

	const { explicitCallTrackCustomEvent } = useAnalytics();

	const { data: productDetails, isLoading } = useProductDetails();

	const { data: productMetadata, isLoading: isProductMetadataLoading } =
		useProductMetadata();

	const bundleId = productDetails?.id;
	const name = productDetails?.name;

	const plans = productDetails?.plans;

	useEffect(() => {
		if (productDetails) {
			explicitCallTrackCustomEvent({
				eventName: events.paywallOpened,
				plans: JSON.stringify(productDetails.plans),
				product_id: productDetails.id,
				product_name: productDetails.name,
			});
		}
	}, [productDetails, explicitCallTrackCustomEvent]);

	const handleTracking = useCallback(
		(eventName: string, params?: Record<string, string | number>) => {
			explicitCallTrackCustomEvent({
				eventName,
				product_id: bundleId,
				product_name: name,
				...params,
			});
		},
		[bundleId, name, explicitCallTrackCustomEvent],
	);

	const {
		currentExamContext: { examDate },
	} = useExamContext();

	const sortedPlans = plans
		? [...plans].sort((a, b) => a.amount - b.amount)
		: [];

	const daysLeft = getExamDaysLeft(examDate);
	const [selectedPlan, setSelectedPlan] = useState(() =>
		sortedPlans?.length > 1
			? getRecommendedPlan(sortedPlans, daysLeft)
			: sortedPlans?.[1],
	);

	if (isLoading || isProductMetadataLoading) {
		return (
			<Box flex={1} justifyContent="center" alignItems="center" minHeight={200}>
				<Loader />
			</Box>
		);
	}

	const {
		features,
		features_mandarin,
		icon: { url },
	} = productDetails!;

	const metadata = productMetadata;
	const premiumContext = metadata ? getPremiumContext(metadata) : null;
	const hasAccess = premiumContext?.hasAccess;

	const recommendedPlan = getRecommendedPlan(sortedPlans, daysLeft);

	const handleClickAccessHere = () => {
		handleClose?.();
		router.push("/home");
	};

	//TODO:Need to be better handled
	const finalFeatures =
		i18n.language === "zh-CN" ? features_mandarin : features;

	return (
		<Container
			maxWidth="md"
			style={{
				//@ts-ignore - web only
				overflow: "auto",
			}}
		>
			<ScrollView
				contentContainerStyle={{
					flexGrow: 1,
					paddingBottom: 24,
				}}
			>
				<IAPModalView
					handleRestorePurchase={handleShowRestorePurchaseDialog}
					features={finalFeatures || []}
					url={url}
					productType={productType || "STUDY_PACK"}
					recommendedPlan={recommendedPlan}
					selectedDate={examDate}
					hasAccess={hasAccess}
					handleTracking={handleTracking}
					selectedPlan={selectedPlan}
					handleClickAccessHere={handleClickAccessHere}
					expiryDate={metadata?.subscription?.endDate}
					setSelectedPlan={setSelectedPlan}
					loading={purchaseInProgress}
					disabled={purchaseInProgress}
					handlePurchase={() => handlePurchase(selectedPlan)}
					sortedPlans={sortedPlans}
					purchasable={true}
					numItems={numItems ? Number(numItems) : 0}
					fromHomePage={fromHomePage}
				/>
			</ScrollView>
		</Container>
	);
};

export default PurchaseContainerBase;
